import React from "react";
import { ArticlesComponent } from "./articlesComponent";
import { ArticleShimmer } from "./articleShimmer";
import { BackwardButton, ForwardButton } from "./blogCarouselButtons";

interface TopArticlesProps {
  articles: any[];
  isLoading: boolean;
  currentIndex: number;
  onPrev: () => void;
  onNext: () => void;
  topArticlesSectionRef: React.RefObject<HTMLDivElement>;
  topArticlesTitleRef: React.RefObject<HTMLParagraphElement>;
  navigationButtonsRef: React.RefObject<HTMLDivElement>;
  desktopCarouselRef: React.RefObject<HTMLDivElement>;
  mobileCarouselRef: React.RefObject<HTMLDivElement>;
  carouselItemsRef: React.RefObject<(HTMLDivElement | null)[]>;
  mobileCarouselItemsRef: React.RefObject<(HTMLDivElement | null)[]>;
}

export const TopArticles: React.FC<TopArticlesProps> = ({
  articles,
  isLoading,
  currentIndex,
  onPrev,
  onNext,
  topArticlesSectionRef,
  topArticlesTitleRef,
  navigationButtonsRef,
  desktopCarouselRef,
  mobileCarouselRef,
  carouselItemsRef,
  mobileCarouselItemsRef,
}) => {
  const showLoading = isLoading && !articles.length;

  const handleCarouselItemRef = (el: HTMLDivElement | null, index: number) => {
    if (carouselItemsRef.current) {
      carouselItemsRef.current[index] = el;
    }
  };

  const handleMobileCarouselItemRef = (
    el: HTMLDivElement | null,
    index: number,
  ) => {
    if (mobileCarouselItemsRef.current) {
      mobileCarouselItemsRef.current[index] = el;
    }
  };

  return (
    <div className="flex max-h-[1037px] flex-col bg-[#022928]">
      <div
        ref={topArticlesSectionRef}
        className="mt-[56px] flex flex-col lg:mx-[75px] lg:flex-row lg:justify-between"
      >
        <div className="mx-[16px] flex-1">
          <p
            ref={topArticlesTitleRef}
            className="font-inter text-[42px] capitalize leading-[100%] tracking-[-0.03em] text-[#D2D2D2] lg:font-dm-sans lg:text-[72px] lg:font-medium lg:leading-[100%]"
          >
            Top Articles
          </p>
        </div>
        <div
          ref={navigationButtonsRef}
          className="hidden flex-row gap-5 lg:flex"
        >
          <BackwardButton onClick={onPrev} />
          <ForwardButton onClick={onNext} />
        </div>
      </div>
      {/* Desktop View */}
      <div className="hidden lg:block">
        <div
          ref={desktopCarouselRef}
          className="relative mx-[75px] mb-[121px] mt-[64px] overflow-hidden"
        >
          {showLoading ? (
            <div className="flex gap-8">
              <div className="w-1/2">
                <ArticleShimmer />
              </div>
              <div className="w-1/2">
                <ArticleShimmer />
              </div>
            </div>
          ) : !articles.length ? (
            <div className="text-xl flex h-64 items-center justify-center text-white">
              No articles available
            </div>
          ) : (
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 50}%)` }}
            >
              {articles.map((article, index) => (
                <div
                  key={article.id}
                  className="w-1/2 flex-shrink-0 px-4"
                  ref={(el) => handleCarouselItemRef(el, index)}
                >
                  <ArticlesComponent article={article} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Mobile View */}
      <div className="block lg:hidden">
        <div
          ref={mobileCarouselRef}
          className="relative mb-[56px] mt-[42px] overflow-hidden"
        >
          {isLoading ? (
            <div className="px-4">
              <ArticleShimmer />
            </div>
          ) : !articles.length ? (
            <div className="text-xl flex h-64 items-center justify-center px-4 text-white">
              No articles available
            </div>
          ) : (
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {articles.map((article, index) => (
                <div
                  key={article.id}
                  className="w-full flex-shrink-0 px-4"
                  ref={(el) => handleMobileCarouselItemRef(el, index)}
                >
                  <ArticlesComponent article={article} />
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Add dot indicators for mobile view */}
        <div className="mb-6 mt-[32px] flex justify-center gap-2">
          {articles.map((_, index) => (
            <button
              key={index}
              className={`h-2 w-2 rounded-full transition-colors ${
                index === currentIndex ? "bg-[#4FBA6D]" : "bg-[#D4D4D4]"
              }`}
              onClick={() => onNext()}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
